import ScrollyStep from "./ScrollyStep";
import DemoChallenge from "./DemoChallenge";
import DemoDriftCards from "./DemoDriftCards";
import DemoThinking from "./DemoThinking";
import DemoIdeas from "./DemoIdeas";
import DemoSmallBets from "./DemoSmallBets";

interface ScrollyDemoSectionProps {
  isLoggedIn: boolean;
}

export default function ScrollyDemoSection({ isLoggedIn }: ScrollyDemoSectionProps) {
  return (
    <div id="demo">
      {/* Section intro */}
      <div
        className="bg-gray-50 border-b border-black/10 py-12 sm:py-16"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 scrolly-reveal">
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: "#F50078" }}>
            See it in action
          </p>
          <h2 className="type-4 font-black uppercase type-display text-black leading-[0.92]">
            Here is how it works
          </h2>
          <p className="text-sm text-gray-500 mt-4 max-w-lg leading-relaxed">
            Five steps. No generic answers. One deliberate collision between your thinking and something unexpected.
          </p>
        </div>
      </div>

      {/* Step 01 */}
      <ScrollyStep
        stepNumber="01"
        label="Define Your Challenge"
        headline="Start with any challenge"
        body="Don't describe the ideal outcome — describe the real problem. The more honest and specific your challenge, the more useful the output. Vague prompts get vague results."
        align="left"
        bg="white"
      >
        <DemoChallenge />
      </ScrollyStep>

      {/* Step 02 */}
      <ScrollyStep
        stepNumber="02"
        label="Pick Your Cards"
        headline="Use the cards as new lenses"
        body="Two random cards — a word and an image. No connection to your problem. That's the point. The Drift interrupts your usual logic path and forces your brain somewhere it wouldn't go alone. Every session is different."
        align="right"
        bg="gray"
      >
        <DemoDriftCards />
      </ScrollyStep>

      {/* Step 03 */}
      <ScrollyStep
        stepNumber="03"
        label="Break Your Patterns"
        headline="Breaking the patterns of conventional thinking"
        body="Thinknado doesn't generate ideas the way a chat bot does. We call this the Drift — a collision between your specific challenge and those random provocations that produces combinations your brain would never allow on its own."
        align="left"
        bg="black"
      >
        <DemoThinking />
      </ScrollyStep>

      {/* Step 04 */}
      <ScrollyStep
        stepNumber="04"
        label="Pick Your Ideas"
        headline="Get ideas that nobody else gets"
        body="Review the ideas that came out of your Drift. Keep the ones with a pulse, skip the ones that do not fit, and narrow toward the few worth acting on."
        align="right"
        bg="white"
      >
        <DemoIdeas />
      </ScrollyStep>

      {/* Step 05 */}
      <ScrollyStep
        stepNumber="05"
        label="Make Your Small Bets"
        headline="Something you can actually try this week"
        body="Each idea becomes a testable action. Not a commitment — an experiment. Something small enough to try quickly, real enough to tell you something. Now it's your turn."
        align="left"
        bg="gray"
      >
        <DemoSmallBets isLoggedIn={isLoggedIn} />
      </ScrollyStep>
    </div>
  );
}
